import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { GungNotificationService } from 'gung-common';
import { first } from 'rxjs';
import { GungModalService } from '../../../../services/gung-modal/gung-modal.service';
import { SalespersonBudget } from '../../models/salesperson-budget';
import { SalespersonBudgetService } from '../../services/salesperson-budget.service';

interface BudgetRow extends SalespersonBudget {
  _key: string;
  _isNew?: boolean;
  _error?: string;
}

type SortKey = 'salespersonId' | 'total' | `month-${number}`;

@Component({
  selector: 'lib-salesperson-budget-table',
  templateUrl: './salesperson-budget-table.component.html',
  styleUrls: ['./salesperson-budget-table.component.scss'],
  standalone: false
})
export class SalespersonBudgetTableComponent implements OnInit {
  protected budgetService = inject(SalespersonBudgetService);
  protected modalService = inject(GungModalService);
  protected translateService = inject(TranslateService);
  protected notificationService = inject(GungNotificationService);

  year: number = new Date().getFullYear();
  months: number[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

  rows: BudgetRow[] = [];
  dirty = new Set<string>();
  loading = false;
  savingAll = false;

  sortKey: SortKey = 'salespersonId';
  sortAsc = true;

  private localKeyCounter = 0;

  ngOnInit(): void {
    this.loadYear();
  }

  loadYear(): void {
    this.loading = true;
    this.rows = [];
    this.dirty.clear();
    this.budgetService
      .getByYear(this.year)
      .pipe(first())
      .subscribe({
        next: list => {
          this.rows = (list || []).map(b => this.toRow(b));
          this.applySort();
          this.loading = false;
        },
        error: err => {
          this.loading = false;
          this.handleError(err);
        }
      });
  }

  prevYear(): void {
    this.year = this.year - 1;
    this.loadYear();
  }

  nextYear(): void {
    this.year = this.year + 1;
    this.loadYear();
  }

  onYearChange(value: number | string): void {
    const parsed = typeof value === 'string' ? parseInt(value, 10) : value;
    if (!parsed || parsed <= 0 || parsed === this.year) {
      return;
    }
    this.year = parsed;
    this.loadYear();
  }

  addRow(): void {
    const row: BudgetRow = {
      _key: this.newLocalKey(),
      _isNew: true,
      salespersonId: '',
      year: this.year,
      monthlyBudgets: this.months.reduce((acc, m) => ({ ...acc, [m]: 0 }), {})
    };
    this.rows = [row, ...this.rows];
    this.dirty.add(row._key);
  }

  markDirty(row: BudgetRow): void {
    this.dirty.add(row._key);
  }

  isDirty(row: BudgetRow): boolean {
    return this.dirty.has(row._key);
  }

  onMonthInput(row: BudgetRow, month: number, value: number | string): void {
    const n = typeof value === 'string' ? parseFloat(value) : value;
    row.monthlyBudgets[month] = isNaN(n as number) ? 0 : Number(n);
    this.markDirty(row);
  }

  onSalespersonIdInput(row: BudgetRow, value: string): void {
    row.salespersonId = (value || '').trim();
    this.markDirty(row);
  }

  getRowTotal(row: BudgetRow): number {
    return this.months.reduce((sum, m) => sum + (Number(row.monthlyBudgets[m]) || 0), 0);
  }

  getColumnTotal(month: number): number {
    return this.rows.reduce((sum, r) => sum + (Number(r.monthlyBudgets[month]) || 0), 0);
  }

  getGrandTotal(): number {
    return this.rows.reduce((sum, r) => sum + this.getRowTotal(r), 0);
  }

  saveRow(row: BudgetRow): void {
    if (!row.salespersonId) {
      row._error = this.translateService.instant('SALESPERSON_ID_REQUIRED');
      return;
    }
    row._error = undefined;
    const payload: SalespersonBudget = {
      id: row.id,
      salespersonId: row.salespersonId,
      year: this.year,
      monthlyBudgets: { ...row.monthlyBudgets }
    };
    this.budgetService
      .save(payload)
      .pipe(first())
      .subscribe({
        next: saved => {
          const updated = this.toRow(saved);
          updated._key = row._key;
          const idx = this.rows.findIndex(r => r._key === row._key);
          if (idx >= 0) {
            this.rows[idx] = updated;
          }
          this.dirty.delete(row._key);
        },
        error: err => {
          row._error = this.extractErrorMessage(err);
          this.handleError(err, /*silent*/ true);
        }
      });
  }

  saveAll(): void {
    const dirtyRows = this.rows.filter(r => this.isDirty(r));
    if (dirtyRows.length === 0) {
      return;
    }
    this.savingAll = true;
    let remaining = dirtyRows.length;
    const done = () => {
      remaining -= 1;
      if (remaining <= 0) {
        this.savingAll = false;
      }
    };
    dirtyRows.forEach(row => {
      if (!row.salespersonId) {
        row._error = this.translateService.instant('SALESPERSON_ID_REQUIRED');
        done();
        return;
      }
      const payload: SalespersonBudget = {
        id: row.id,
        salespersonId: row.salespersonId,
        year: this.year,
        monthlyBudgets: { ...row.monthlyBudgets }
      };
      this.budgetService
        .save(payload)
        .pipe(first())
        .subscribe({
          next: saved => {
            const updated = this.toRow(saved);
            updated._key = row._key;
            const idx = this.rows.findIndex(r => r._key === row._key);
            if (idx >= 0) {
              this.rows[idx] = updated;
            }
            this.dirty.delete(row._key);
            row._error = undefined;
            done();
          },
          error: err => {
            row._error = this.extractErrorMessage(err);
            done();
          }
        });
    });
  }

  deleteRow(row: BudgetRow): void {
    if (row._isNew || !row.id) {
      this.removeRow(row);
      return;
    }
    this.modalService
      .openConfirmYesNoModal(
        this.translateService.instant('DELETE'),
        this.translateService.instant('CONFIRM_DELETE_BUDGET'),
        { size: 'sm' }
      )
      .then(confirmed => {
        if (!confirmed) {
          return;
        }
        this.budgetService
          .delete(row.id!)
          .pipe(first())
          .subscribe({
            next: () => this.removeRow(row),
            error: err => this.handleError(err)
          });
      });
  }

  exportExcel(): void {
    this.budgetService
      .exportXlsx(this.year)
      .pipe(first())
      .subscribe({
        error: err => this.handleError(err)
      });
  }

  onImportFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    // Reset so the same file can be chosen again later.
    input.value = '';
    if (!file) {
      return;
    }
    this.modalService
      .openConfirmYesNoModal(
        this.translateService.instant('IMPORT_EXCEL'),
        this.translateService.instant('CONFIRM_IMPORT_OVERWRITES')
      )
      .then(confirmed => {
        if (!confirmed) {
          return;
        }
        this.budgetService
          .importXlsx(this.year, file)
          .pipe(first())
          .subscribe({
            next: result => {
              this.notificationService.notify(
                this.translateService.instant('IMPORT_EXCEL'),
                this.translateService.instant('IMPORT_RESULT_SUMMARY', {
                  imported: result.imported,
                  skipped: result.skipped
                }),
                'success'
              );
              this.loadYear();
            },
            error: err => this.handleError(err)
          });
      });
  }

  toggleSort(key: SortKey): void {
    if (this.sortKey === key) {
      this.sortAsc = !this.sortAsc;
    } else {
      this.sortKey = key;
      this.sortAsc = false; // first click sorts descending
    }
    this.applySort();
  }

  sortKeyForMonth(month: number): SortKey {
    return `month-${month}`;
  }

  private applySort(): void {
    const dir = this.sortAsc ? 1 : -1;
    const key = this.sortKey;
    this.rows = [...this.rows].sort((a, b) => {
      if (key === 'total') {
        return (this.getRowTotal(a) - this.getRowTotal(b)) * dir;
      }
      if (typeof key === 'string' && key.startsWith('month-')) {
        const m = Number(key.slice('month-'.length));
        return ((Number(a.monthlyBudgets[m]) || 0) - (Number(b.monthlyBudgets[m]) || 0)) * dir;
      }
      return (a.salespersonId || '').localeCompare(b.salespersonId || '', undefined, { numeric: true }) * dir;
    });
  }

  private removeRow(row: BudgetRow): void {
    this.rows = this.rows.filter(r => r._key !== row._key);
    this.dirty.delete(row._key);
  }

  private toRow(b: SalespersonBudget): BudgetRow {
    const monthly: { [m: number]: number } = {};
    for (const m of this.months) {
      monthly[m] = Number(b.monthlyBudgets?.[m]) || 0;
    }
    return {
      ...b,
      monthlyBudgets: monthly,
      _key: b.id || this.newLocalKey()
    };
  }

  private newLocalKey(): string {
    this.localKeyCounter += 1;
    return `new-${this.localKeyCounter}-${Date.now()}`;
  }

  private extractErrorMessage(err: any): string {
    if (err instanceof HttpErrorResponse) {
      return err.error?.message || err.error?.error || err.message;
    }
    return err?.message || 'Error';
  }

  private handleError(err: any, silent = false): void {
    if (!(err instanceof HttpErrorResponse)) {
      if (!silent) {
        this.notificationService.notify(
          this.translateService.instant('ERROR'),
          this.translateService.instant('SOMETHING_WENT_WRONG'),
          'error'
        );
      }
      return;
    }
    let message: string;
    switch (err.status) {
      case 400:
        message = this.extractErrorMessage(err);
        break;
      case 403:
        message = this.translateService.instant('FORBIDDEN');
        break;
      case 404:
        message = this.translateService.instant('NOT_FOUND');
        break;
      default:
        message = this.translateService.instant('SOMETHING_WENT_WRONG');
    }
    if (!silent) {
      this.notificationService.notify(this.translateService.instant('ERROR'), message, 'error');
    }
  }
}
